import type {
    AppName,
    AppPlatform,
    Audience,
    HostContext,
    HostUserContext,
    HostLocaleContext,
    HostScenarioLifecycle,
} from '@fluidx/office-fluid-types';
import { Locale, resolveLocale } from '@fluidx/utilities';
import { getClientVersion } from 'owa-config';
import { timestamp } from 'owa-datetime';
import { getGuid } from 'owa-guid';
import { getCurrentLanguage } from 'owa-localize';
import { getAudience, getUser } from './userSettings';
import { OUTLOOK_APP_NAME, OWA_APP_PLATFORM, OUTLOOK_WEB_APP_NAME } from '../../constants';

export class AppHostContext implements HostContext {
    readonly audienceScope: Audience = getAudience();
    readonly user = getUser();
    readonly owaCorrelationId: string;
    readonly scenarioStartTime: number;

    constructor(
        private readonly sessionId: string,
        private readonly scenarioLifecycle: HostScenarioLifecycle,
        private readonly scenarioName: string,
        private readonly scenarioEntryPoint: string,
        scenarioStartTime?: number,
        correlationId?: string
    ) {
        this.owaCorrelationId = correlationId || getGuid();
        this.scenarioStartTime = scenarioStartTime || timestamp();
    }

    public get HostContext() {
        return this;
    }

    public get audience(): Audience {
        return this.audienceScope;
    }

    public get hostCorrelationId(): string {
        return this.owaCorrelationId;
    }

    public get hostSessionId(): string {
        return this.sessionId;
    }

    public get hostAppName(): AppName {
        return OUTLOOK_APP_NAME;
    }

    public get hostAppPlatform(): AppPlatform {
        return OWA_APP_PLATFORM;
    }

    public get hostName(): string {
        return OUTLOOK_WEB_APP_NAME;
    }

    public get userContext(): HostUserContext {
        return { user: this.user };
    }

    public get hostScenarioLifecycle(): HostScenarioLifecycle {
        return this.scenarioLifecycle;
    }

    public get hostScenarioName(): string {
        return this.scenarioName;
    }

    public get hostScenarioEntryPoint(): string {
        return this.scenarioEntryPoint;
    }

    public get hostScenarioStartTime(): number {
        return this.scenarioStartTime;
    }

    public get hostAppVersion(): string {
        return getClientVersion();
    }

    public get hostLocaleContext(): HostLocaleContext {
        const locale = getCurrentLanguage();
        const fluidLocale: Locale = resolveLocale(locale);
        return {
            uiLanguage: fluidLocale,
        };
    }
}
